// src/pages/Customers.js
import React, { useEffect, useState } from "react";
import { Table, Button, message, Input, Space, Row, Col } from "antd";
import { EditOutlined, PlusOutlined, DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import CustomerForm from "./CustomerForm";
import API from "../../api";
import CustomTable from "../../components/Table/CustomTable";
import ConfirmationModal from "../../components/ConfirmationModal";

export default function Customers() {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [paginationInfo, setPaginationInfo] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [loading, setLoading] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [userRole, setUserRole] = useState("");


  useEffect(() => {
    const userData = localStorage.getItem("userData");
    const data = JSON.parse(userData);
    setUserRole(data);
  }, []);


  // Fetch customers from API
  const getCustomers = async () => {
    try {
      setLoading(true)
      const { current, pageSize } = paginationInfo;
      const response = await API.get("/customers/paginate", { params: { page: current, limit: pageSize, search: searchQuery, } });
      const { customers: getCustomers, pagination } = response.data;
      setCustomers(getCustomers);
      setPaginationInfo((prev) => ({
        ...prev,
        total: pagination.total,
      }));
    } catch (err) {
      console.error("Error fetching customers:", err);
      message.error("Failed to fetch customers. Please try again.");
    } finally {
      setLoading(false)
    }
  };

  useEffect(() => {
    getCustomers();
  }, [paginationInfo.current, paginationInfo.pageSize, searchQuery]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPaginationInfo((prev) => ({ ...prev, current: 1 }));
  };

  const handleEditClick = (customer) => {
    setIsEdit(true);
    setSelectedCustomer({
      ...customer,
    });
    setOpenDrawer(true);
  };
  // Handle the edited customer data
  const handleAddOrEditCustomer = async (customerData) => {
    try {
      if (isEdit) {
        // Update Customer
        const res = await API.put(
          `/customers/${selectedCustomer?._id}`,
          customerData
        );
        if (res.status == 200) {
          message.success("Customer updated successfully!");
          setOpenDrawer(false);
        }
      } else {
        // Add new Customer
        const res = await API.post("/customers", customerData);
        if (res.status == 200) {
          message.success("Customer added successfully!");
          setOpenDrawer(false);
        }
      }
      getCustomers(); // Refresh the Customer list
    } catch (error) {
      console.error("Error saving Customer:", error);
      message.error(error.response.data.message);
    }
  };

  const handleDeleteCustomer = async () => {
    try {
      await API.delete(`/customers/${customerToDelete?._id}`);
      setCustomers(
        customers.filter((customer) => customer?._id !== customerToDelete?._id)
      );
      setIsDeleteModalVisible(false); // Close the modal after deletion
      message.success("Customer Deleted Successfully");
    } catch (err) {
      console.error("Error deleting customer:", err);
      message.error(err.response.data.message);
    }
  };

  // Table columns configuration
  const columns = [
    {
      title: "Index",
      key: "index",
      render: (_, __, index) =>
        (paginationInfo.current - 1) * paginationInfo.pageSize + index + 1,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
    },
    // {
    //   title: "GST",
    //   dataIndex: "gstNumber",
    //   key: "gstNumber",
    // },
    {
      title: "Address",
      key: "address",
      render: (record) => {
        const { address } = record;
        return address
          ? `${address?.street}, ${address?.city}, ${address?.state}, ${address?.pincode}`
          : "No Address Provided";
      },
    },

    userRole.editPermission == true && {

      title: "Actions",
      key: "actions",
      align: "center",
      render: (record) => (
        <div>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEditClick(record)}
          />
          {/* <Button
            icon={<DeleteOutlined />}
            style={{ marginLeft: 8 }}
            onClick={() => {
              setCustomerToDelete(record);
              setIsDeleteModalVisible(true);
            }}
            danger
          /> */}
        </div>
      ),
    },

  ];

  return (
    <div  style={{
      padding: "20px",
      background: "#fff",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    }}>
      {/* Only show the "Add New" button */}
      <Row gutter={16} style={{ marginBottom: 16, justifyContent: 'flex-end', marginRight: 10 }}>
        <Col xs={24} sm={16} md={18} lg={20}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setSelectedCustomer(null);
                setIsEdit(false);
                setOpenDrawer(true);
              }}
            >
              Add New
            </Button>

            <Input
              placeholder="Search customers"
              prefix={<SearchOutlined />}
              value={searchQuery}
              onChange={handleSearch}
              style={{ width: '100%' }} // Make search input responsive
            />
          </Space>
        </Col>
      </Row>

      {/* Customer Form Drawer */}
      {openDrawer && (
        <CustomerForm
          open={openDrawer}
          setOpen={setOpenDrawer}
          isEdit={isEdit}
          initialValue={isEdit ? selectedCustomer : null}
          onSubmit={handleAddOrEditCustomer}
        />
      )}

      {/* Custom Table */}
      <CustomTable
        columns={columns}
        dataSource={customers}  // Ensure the filtered data is passed here
        onPaginationChange={(pagination) => {
          setPaginationInfo({
            current: pagination.current,
            pageSize: pagination.pageSize,
          });
        }}
        loading={loading}
        style={{ padding: '0 20px' }}
        total={paginationInfo.total}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        visible={isDeleteModalVisible}
        onConfirm={handleDeleteCustomer}
        onCancel={() => setIsDeleteModalVisible(false)}
        title="Confirm Deletion"
        content="Are you sure you want to delete this customer? This action cannot be undone."
      />
    </div>
  );
}
